import { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useFetch, Field, Modal, money } from "@/lib/ui";
import { useLang } from "@/context/LangContext";
import { Empty } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ShoppingCart, PackagePlus, AlertTriangle } from "lucide-react";

const CATEGORIES = ["Uniforme", "Maillot", "Chaussettes", "Cravate", "PUS", "Valise", "Badge", "Livre", "Tablette", "App",
  "Frais d'activité", "Frais de graduation", "Frais examen d'État", "Frais divers", "Matériel", "Fourniture", "Autre"];
const FEE_CATS = ["Frais d'activité", "Frais de graduation", "Frais examen d'État", "Frais divers", "App"];
const GRADES = ["Chez-Kids 1", "Chez-Kids 2", "Chez-Kids 3", "CP1", "CP2", "CE1", "CE2", "CM1", "CM2",
  "7ème Année Fondamentale", "8ème Année Fondamentale", "9ème Année Fondamentale", "NS1", "NS2", "NS3", "NS4"];

export default function Inventory({ canManage, canEdit }) {
  const { L } = useLang();
  const { data, loading, reload } = useFetch(() => api.get("/inventory"));
  const [open, setOpen] = useState(false);
  const [sellItem, setSellItem] = useState(null);
  const EMPTY = { name: "", category: "Uniforme", sellable: true, quantity: "", unit_price: "", min_stock: "", for_class: "", track_stock: true };
  const [f, setF] = useState(EMPTY);
  const [sell, setSell] = useState({ quantity: "", method: "cash", buyer: "" });
  const isFee = FEE_CATS.includes(f.category);
  const isBook = f.category === "Livre";

  const items = data?.items || [];
  const salesTotal = data?.sales_total || 0;

  const save = async () => {
    if (!f.name.trim()) { toast.error(L("Name is required", "Le nom est requis")); return; }
    try {
      await api.post("/inventory", {
        name: f.name.trim(), category: f.category, sellable: f.sellable,
        quantity: parseInt(f.quantity || "0"), unit_price: parseFloat(f.unit_price || "0"),
        min_stock: parseInt(f.min_stock || "0"), for_class: f.for_class || null,
        track_stock: isFee ? false : f.track_stock,
      });
      toast.success(L("Item added", "Article ajouté")); setOpen(false); setF(EMPTY); reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const restock = async (item) => {
    const v = window.prompt(L("New quantity in stock:", "Nouvelle quantité en stock :"), item.quantity);
    if (v === null) return;
    try { await api.raw.patch(`/inventory/${item.id}`, { quantity: parseInt(v || "0") }); reload(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (it) => { if (!window.confirm(L(`Are you sure you want to delete ${it.name}?`, `Êtes-vous sûr de vouloir supprimer ${it.name} ?`))) return; try { await api.del(`/inventory/${it.id}`); reload(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const doSell = async () => {
    const qty = parseInt(sell.quantity || "0");
    if (qty <= 0) { toast.error(L("Enter a quantity", "Entrez une quantité")); return; }
    try {
      const r = await api.post(`/inventory/${sellItem.id}/sell`, { quantity: qty, method: sell.method, buyer: sell.buyer || null });
      toast.success(L(`Sold — ${money(r.sale.total)} recorded`, `Vendu — ${money(r.sale.total)} enregistré`));
      setSellItem(null); setSell({ quantity: "", method: "cash", buyer: "" }); reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (loading) return <Empty title="Loading…" />;
  return (
    <div className="animate-fade-up">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading font-extrabold text-2xl">{L("Inventory", "Inventaire")}</h2>
          <p className="text-muted-foreground text-sm">{L("Track uniforms and school materials. Sales auto-deduct stock and record revenue.", "Suivez les uniformes et le matériel. Les ventes déduisent le stock et enregistrent le revenu.")}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-xs text-muted-foreground">{L("Total sales", "Total des ventes")}</p>
            <p className="font-heading font-extrabold text-xl text-primary" data-testid="inv-sales-total">{money(salesTotal)}</p>
          </div>
          {canEdit && <Button onClick={() => { setF(EMPTY); setOpen(true); }} data-testid="add-inventory-btn"><Plus className="h-4 w-4 mr-1" />{L("Add Item", "Ajouter")}</Button>}
        </div>
      </div>

      {items.length === 0 ? <Empty title={L("No inventory yet", "Inventaire vide")} hint={L("Add uniforms (maillots, chaussettes, cravates, PUS) or other school materials.", "Ajoutez des uniformes (maillots, chaussettes, cravates, PUS) ou du matériel.")} /> : (
        <div className="border border-border rounded-lg overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>{L("Item", "Article")}</TableHead><TableHead>{L("Category", "Catégorie")}</TableHead>
              <TableHead>{L("In stock", "En stock")}</TableHead><TableHead>{L("Unit price", "Prix unitaire")}</TableHead>
              <TableHead>{L("Type", "Type")}</TableHead><TableHead className="text-right">{L("Actions", "Actions")}</TableHead>
            </TableRow></TableHeader>
            <TableBody>{items.map((it) => (
              <TableRow key={it.id} data-testid={`inv-row-${it.id}`}>
                <TableCell className="font-medium">{it.name}{it.for_class ? <span className="block text-xs text-muted-foreground">{it.for_class}</span> : null}</TableCell>
                <TableCell><Badge variant="secondary" className="font-normal">{it.category}</Badge></TableCell>
                <TableCell>
                  {it.track_stock === false ? <span className="text-muted-foreground">—</span> : <>
                    <span className={it.low_stock ? "text-destructive font-semibold" : ""} data-testid={`inv-qty-${it.id}`}>{it.quantity}</span>
                    {it.low_stock && <AlertTriangle className="h-3.5 w-3.5 text-destructive inline ml-1" title={L("Low stock", "Stock bas")} />}
                  </>}
                </TableCell>
                <TableCell>{it.sellable ? money(it.unit_price) : "—"}</TableCell>
                <TableCell>{it.sellable ? <Badge className="font-normal">{L("For sale", "En vente")}</Badge> : <Badge variant="outline" className="font-normal">{L("Not for sale", "Non vendable")}</Badge>}</TableCell>
                <TableCell className="text-right space-x-1">
                  {canEdit && it.sellable && <Button size="sm" onClick={() => { setSellItem(it); setSell({ quantity: "1", method: "cash", buyer: "" }); }} data-testid={`sell-${it.id}`}><ShoppingCart className="h-4 w-4 mr-1" />{L("Sell", "Vendre")}</Button>}
                  {canEdit && <Button size="icon" variant="ghost" onClick={() => restock(it)} data-testid={`restock-${it.id}`}><PackagePlus className="h-4 w-4" /></Button>}
                  {canManage && <Button size="icon" variant="ghost" onClick={() => del(it)} data-testid={`del-inv-${it.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                </TableCell>
              </TableRow>))}</TableBody>
          </Table>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={L("Add inventory item", "Ajouter un article")}
        footer={<Button onClick={save} data-testid="save-inventory-btn">{L("Save", "Enregistrer")}</Button>}>
        <Field label={L("Name", "Nom")}><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={L("e.g. Maillot Taille M", "ex. Maillot Taille M")} data-testid="inv-name-input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={L("Category", "Catégorie")}>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v, sellable: FEE_CATS.includes(v) ? true : f.sellable })}>
              <SelectTrigger data-testid="inv-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {!isFee && <Field label={L("Quantity in stock", "Quantité en stock")}><Input type="number" min="0" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="inv-qty-input" /></Field>}
        </div>
        {isBook && (
          <Field label={L("For which class", "Pour quelle classe")}>
            <Select value={f.for_class} onValueChange={(v) => setF({ ...f, for_class: v })}>
              <SelectTrigger data-testid="inv-class-select"><SelectValue placeholder={L("Select class", "Choisir la classe")} /></SelectTrigger>
              <SelectContent>{GRADES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        )}
        {isFee ? (
          <p className="text-xs text-muted-foreground my-1">{L("This is a fee — no stock tracking. Selling it records the revenue only.", "Ceci est un frais — pas de suivi de stock. La vente enregistre uniquement le revenu.")}</p>
        ) : (
          <div className="flex items-center gap-2 my-1">
            <input type="checkbox" checked={f.sellable} onChange={(e) => setF({ ...f, sellable: e.target.checked })} data-testid="inv-sellable-check" id="sellable" />
            <label htmlFor="sellable" className="text-sm">{L("This item can be sold (deducts stock + records revenue)", "Cet article peut être vendu (déduit le stock + enregistre le revenu)")}</label>
          </div>
        )}
        {(f.sellable || isFee) && (
          <div className="grid grid-cols-2 gap-3">
            <Field label={isFee ? L("Fee amount ($)", "Montant du frais ($)") : L("Unit price ($)", "Prix unitaire ($)")}><Input type="number" min="0" step="0.01" value={f.unit_price} onChange={(e) => setF({ ...f, unit_price: e.target.value })} data-testid="inv-price-input" /></Field>
            {!isFee && <Field label={L("Low-stock alert at", "Alerte stock bas à")}><Input type="number" min="0" value={f.min_stock} onChange={(e) => setF({ ...f, min_stock: e.target.value })} data-testid="inv-minstock-input" /></Field>}
          </div>
        )}
      </Modal>

      <Modal open={!!sellItem} onClose={() => setSellItem(null)} title={sellItem ? `${L("Sell", "Vendre")}: ${sellItem.name}` : ""}
        footer={<Button onClick={doSell} data-testid="confirm-sell-btn"><ShoppingCart className="h-4 w-4 mr-1" />{L("Record sale", "Enregistrer la vente")}</Button>}>
        {sellItem && (
          <>
            <p className="text-sm text-muted-foreground mb-2">{L("In stock", "En stock")}: <b>{sellItem.quantity}</b> · {L("Unit price", "Prix unitaire")}: <b>{money(sellItem.unit_price)}</b></p>
            <div className="grid grid-cols-2 gap-3">
              <Field label={L("Quantity", "Quantité")}><Input type="number" min="1" max={sellItem.quantity} value={sell.quantity} onChange={(e) => setSell({ ...sell, quantity: e.target.value })} data-testid="sell-qty-input" /></Field>
              <Field label={L("Method", "Méthode")}>
                <Select value={sell.method} onValueChange={(v) => setSell({ ...sell, method: v })}>
                  <SelectTrigger data-testid="sell-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="cash">{L("Cash", "Espèces")}</SelectItem><SelectItem value="zelle">Zelle</SelectItem></SelectContent>
                </Select>
              </Field>
            </div>
            <Field label={L("Buyer (optional)", "Acheteur (optionnel)")}><Input value={sell.buyer} onChange={(e) => setSell({ ...sell, buyer: e.target.value })} data-testid="sell-buyer-input" /></Field>
            <p className="text-sm font-medium">{L("Total", "Total")}: {money((parseFloat(sell.quantity || "0") || 0) * (sellItem.unit_price || 0))}</p>
          </>
        )}
      </Modal>
    </div>
  );
}
